import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useServices } from "../../context/ServiceProvider";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Input,
  Typography,
  Button,
  List,
  Spin,
  Empty,
  Alert,
} from "antd";
import { CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useRecents } from "../../hooks/useRecents";
import CreateQualificationStepModal from "./CreateQualificationStepModal";
import CloneQualificationStepModal from "./CloneQualificationStepModal";
import { resetBreadcrumbs } from "../../store/slices/navigationSlice";

export default function ProjectQualStepList() {
  const { projectQualStep } = useServices();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const { record } = useRecents({ storageKey: "RECENTS::v1", maxItems: 50 });
  const dispatch = useDispatch();

  // Dropdown search state
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = React.useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await projectQualStep.fetchAllQualSteps();
      setRecords(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [projectQualStep]);

  // Dropdown search with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    const term = (query || "").trim();
    if (term.length < 3) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await projectQualStep.searchQualSteps(term);
        setSearchResults(Array.isArray(data) ? data : []);
        setSearchOpen(true);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, projectQualStep]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    dispatch(resetBreadcrumbs());
  }, [dispatch]);

  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "Name",
        key: "Name",
        width: "15%",
        ellipsis: true,
        sorter: (a, b) => a.Name.localeCompare(b.Name),
        render: (text, rec) => (
          <Link
            to={{ pathname: `/pqs/${rec.Id}` }}
            state={{ crumbLabel: rec.Name }}
            onClick={() => {
              record({
                id: String(rec.Id),
                title: String(text || rec.Name || rec.Id),
                url: `/pqs/${rec.Id}`,
                type: "Project Qual Step",
              });
            }}
          >
            {text || rec.Id}
          </Link>
        ),
      },
      {
        title: "Project Objective",
        dataIndex: "Project_Objective__r.Name",
        key: "Project Objective",
        width: "15%",
        ellipsis: true,
        sorter: (a, b) => {
          const poA = a.Project_Objective__r;
          const poB = b.Project_Objective__r;
          if (!poA) return -1;
          if (!poB) return 1;
          return poA.Name.localeCompare(poB.Name);
        },
        render: (text, rec) => {
          const po = rec.Project_Objective__r;
          if (!po) return "-";
          return (
            <Link
              to={{ pathname: `/po/${po.Id}` }}
              state={{ crumbLabel: po.Name }}
              onClick={() =>
                record({
                  id: String(po.Id),
                  title: String(text || po.Name || po.Id),
                  url: `/po/${po.Id}`,
                  type: "Project Objective",
                })
              }
            >
              {text || po.Name}
            </Link>
          );
        },
      },
      {
        title: "Funnel",
        dataIndex: "Funnel__c",
        key: "Funnel",
        width: "4%",
        render: (text, rec) => text || rec.Funnel__c || "-",
      },
      {
        title: "Step Number",
        dataIndex: "Step_Number__c",
        key: "Step Number",
        width: "5%",
        render: (text, rec) => text || rec.Step_Number__c || "-",
      },
      {
        title: "Status",
        dataIndex: "Project_Qualification_Step_Status__c",
        key: "Status",
        width: "4%",
        ellipsis: true,
        sorter: (a, b) =>
          a.Project_Qualification_Step_Status__c.localeCompare(
            b.Project_Qualification_Step_Status__c,
          ),
        render: (text, rec) => {
          const qualStepStatus = rec.Project_Qualification_Step_Status__c;
          if (!qualStepStatus) return "-";
          return text || qualStepStatus;
        },
      },
      {
        title: "Qualification Step",
        dataIndex: "Qualification_Step__r.Name",
        key: "Qualification Step",
        width: "14%",
        ellipsis: true,
        sorter: (a, b) =>
          a.Qualification_Step__r.Name.localeCompare(
            b.Qualification_Step__r.Name,
          ),
        render: (text, rec) => {
          const qualStep = rec.Qualification_Step__r;
          if (!qualStep) return null;
          return text || qualStep.Name;
        },
      },
    ],
    [navigate, record],
  );

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    loadAll(); // Reload the list after successful creation
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-4">
        <Typography.Title level={3} style={{ margin: 0 }}>
          Project Qualification Steps
        </Typography.Title>
        <div className="flex items-center gap-3">
          <Button
            type="primary"
            onClick={() => setShowCreateModal(true)}
            icon={
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            }
          >
            Create
          </Button>
          <Button
            type="default"
            onClick={() => setShowCloneModal(true)}
            icon={<CopyOutlined />}
          >
            Clone Step
          </Button>
          <div className="relative" style={{ maxWidth: 360 }}>
            <Input.Search
              placeholder="Search project qual steps"
              allowClear
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setSearchOpen(false)}
            />
            {searchOpen && query.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                {searchLoading ? (
                  <div className="p-3 text-sm text-gray-500">
                    <Spin size="small" /> Searching…
                  </div>
                ) : query.length < 3 ? (
                  <div className="p-3 text-sm text-gray-500">
                    Enter at least 3 characters to search
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-3">
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No qual steps found"
                    />
                  </div>
                ) : (
                  <List
                    dataSource={searchResults}
                    size="large"
                    renderItem={(item) => (
                      <List.Item
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        onMouseDown={() => {
                          setQuery("");
                          setSearchResults([]);
                          setSearchOpen(false);
                          record({
                            id: String(item.Id),
                            title: String(item.Name || item.Id),
                            url: `/pqs/${item.Id}`,
                            type: "ProjectQualStep",
                          });
                          navigate(`/pqs/${item.Id}`);
                        }}
                      >
                        <div className="w-full">
                          <div className="text-gray-900 text-sm font-medium">
                            {item.Name}
                          </div>
                          <div className="text-xs text-gray-500">{item.Id}</div>
                        </div>
                      </List.Item>
                    )}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clone Qualification Step Modal */}
      <CloneQualificationStepModal
        visible={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        onSuccess={() => {
          setShowCloneModal(false);
          loadAll(); // Refresh the list
        }}
      />
      <Table
        loading={loading}
        rowKey={(r) => r.Id}
        dataSource={records}
        columns={columns}
        scroll={{ y: "calc(100vh - 280px)" }}
        pagination={{
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30", "50", "100"],
          onShowSizeChange: (current, size) => setPageSize(size),
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
      {!loading && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Alert
            type="info"
            message={
              <Typography.Text>
                <strong>NOTE :</strong>
                <> Only first 1000 records are being Displayed.</>
              </Typography.Text>
            }
            icon={<InfoCircleOutlined />}
            style={{ width: "fit-content", alignSelf: "flex-end" }}
            showIcon
          />
        </div>
      )}
      <CreateQualificationStepModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
}
